/**
 * Snapshot-based undo/redo for FlowDesigner.
 *
 * Captures {nodes, edges} before each mutation so Cmd+Z / Cmd+Shift+Z can
 * restore prior states.  Backend is synced via a debounced
 * PUT /workflows/:id/draft-state call.
 *
 * @module useUndoHistory
 */
import { useCallback, useRef, useState } from 'react';
import type { WorkflowNode } from '../../../../types/workflow';
import type { WorkflowEdge } from '../../../../types/workflow';
import { isTempNodeId } from '../utils';
import {
  buildNodeQuickCreateHandlers,
  type WorkflowNodeType,
  type QuickCreateTarget,
  type BranchType,
} from '../helpers';
import { serializeNodeData } from '../clipboard-utils';

const MAX_SNAPSHOTS = 50;

/**
 * Optional side-effects attached to an undo snapshot. When the snapshot is
 * restored (via Cmd+Z), the hook fires the corresponding effect so parts of
 * the system outside the canvas (e.g. the proposal status in the DB) can
 * roll back in step. Keep the payload serializable — snapshots live in refs
 * but we want the shape to stay friendly to future persistence.
 */
export type AttachedEffect =
  | { kind: 'revert-proposal'; proposalId: string; proposalTitle: string };

interface SerializedSnapshot {
  description: string;
  nodes: Array<{
    id: string;
    type: string;
    data: Record<string, unknown>;
  }>;
  edges: Array<{
    id: string;
    source: string;
    target: string;
    type?: string;
    edgeType?: string;
  }>;
  /**
   * Effects to run AFTER the canvas state is restored from this snapshot —
   * e.g. flip a proposal back to pending when the user undoes its accept.
   */
  attachedEffects?: AttachedEffect[];
}

interface RehydrateHandlers {
  handleNodeConfig: (node: WorkflowNode) => void;
  handleQuickCreateNode: (sourceNodeId: string, nextType: QuickCreateTarget) => void;
  handleBranchQuickCreate: (
    sourceNodeId: string,
    nodeType: QuickCreateTarget,
    branchType: BranchType
  ) => void;
  handleCacheClick: (nodeId: string, event: React.MouseEvent) => void;
  handlePlaybookClick: (nodeId?: string) => void;
  enableApiSequence: boolean;
  enableSandboxAction: boolean;
  enableMcp: boolean;
}

interface UseUndoHistoryOptions {
  getNodes: () => WorkflowNode[];
  getEdges: () => WorkflowEdge[];
  setNodes: (nodes: WorkflowNode[] | ((prev: WorkflowNode[]) => WorkflowNode[])) => void;
  setEdges: (edges: WorkflowEdge[] | ((prev: WorkflowEdge[]) => WorkflowEdge[])) => void;
  syncDraftState: (state: {
    nodes: Array<{
      id: string;
      nodeType: string;
      orderIndex: number;
      configJson: Record<string, unknown>;
      canonicalPageId?: string | null;
      canonicalActionId?: string | null;
    }>;
    edges: Array<{
      id: string;
      sourceNodeId: string;
      targetNodeId: string;
      edgeType?: string;
    }>;
  }) => Promise<unknown>;
  rehydrateHandlers: RehydrateHandlers;
  /**
   * Fired once per attached effect when an UNDO restores a snapshot. The
   * hook doesn't know how to run these — the caller decides (e.g. POSTs to
   * the revert-proposal endpoint for `kind: 'revert-proposal'`).
   *
   * Asymmetry by design: effects fire only on undo, NOT on redo. Redoing an
   * accept restores the canvas to the post-accept state but the proposal
   * stays `pending` server-side. Re-accepting requires going through the
   * review UI so the user explicitly re-confirms — a silent server-side
   * re-flip would be surprising. If this becomes a limitation in practice,
   * add a mirrored `onRedoAttachedEffect` rather than reusing this one.
   */
  onAttachedEffect?: (effect: AttachedEffect) => void;
}

function serializeSnapshot(
  description: string,
  nodes: WorkflowNode[],
  edges: WorkflowEdge[]
): SerializedSnapshot {
  // Filter out temp (in-flight optimistic) nodes
  const persistedNodes = nodes.filter((n) => !isTempNodeId(n.id));
  return {
    description,
    nodes: persistedNodes.map((n) => ({
      id: n.id,
      type: n.type ?? 'action',
      data: serializeNodeData(n.data as Record<string, unknown>),
    })),
    edges: edges
      .filter((e) => !isTempNodeId(e.source) && !isTempNodeId(e.target))
      .map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        type: e.type,
        edgeType: e.edgeType,
      })),
  };
}

function rehydrateNodes(snapshot: SerializedSnapshot, handlers: RehydrateHandlers): WorkflowNode[] {
  return snapshot.nodes.map((n) => {
    const data: Record<string, unknown> = {
      ...n.data,
      isPersisted: true,
      executionStatus: 'idle',
      executionMessage: '',
      isExecuting: false,
      onConfigClick: () =>
        handlers.handleNodeConfig({
          id: n.id,
          type: n.type,
          data: n.data,
        }),
      onCacheClick: (event: React.MouseEvent) => handlers.handleCacheClick(n.id, event),
      playbookStepCount: Array.isArray(n.data?.playbookSteps)
        ? (n.data.playbookSteps as unknown[]).length
        : undefined,
      onPlaybookClick: () => handlers.handlePlaybookClick(n.id),
      ...buildNodeQuickCreateHandlers({
        nodeType: n.type as WorkflowNodeType,
        nodeId: n.id,
        allowTemp: true,
        handleQuickCreate: handlers.handleQuickCreateNode,
        handleBranchCreate: handlers.handleBranchQuickCreate,
        enableApiSequence: handlers.enableApiSequence,
        enableSandboxAction: handlers.enableSandboxAction,
        enableMcp: handlers.enableMcp,
      }),
    };

    if (n.type === 'seed' && n.data?.seedWorkflowId) {
      data.onViewFlow = () =>
        window.open(`/flows/${n.data.seedWorkflowId}`, '_blank', 'noopener,noreferrer');
    }

    // `call_flow` names its child `flowId`; see the matching arm in
    // useCanvasHydration. Both paths must set this or the node's
    // "View called flow" button never renders.
    if (n.type === 'call_flow' && n.data?.flowId) {
      data.onViewFlow = () =>
        window.open(`/flows/${n.data.flowId}`, '_blank', 'noopener,noreferrer');
    }

    if (n.type === 'condition') {
      data.enableApiSequence = handlers.enableApiSequence;
    }

    return { id: n.id, type: n.type, data };
  });
}

function rehydrateEdges(snapshot: SerializedSnapshot): WorkflowEdge[] {
  return snapshot.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    type: e.type,
    edgeType: e.edgeType,
  }));
}

/** Convert snapshot nodes to the backend syncDraftState payload format. */
function toBackendPayload(snapshot: SerializedSnapshot) {
  return {
    nodes: snapshot.nodes.map((n, i) => ({
      id: n.id,
      nodeType: n.type,
      orderIndex: i,
      configJson: n.data as Record<string, unknown>,
      canonicalPageId: (n.data as Record<string, unknown>).canonicalPageId as
        | string
        | null
        | undefined,
      canonicalActionId: (n.data as Record<string, unknown>).canonicalActionId as
        | string
        | null
        | undefined,
    })),
    edges: snapshot.edges.map((e) => ({
      id: e.id,
      sourceNodeId: e.source,
      targetNodeId: e.target,
      edgeType: e.edgeType ?? e.type ?? 'default',
    })),
  };
}

export function useUndoHistory({
  getNodes,
  getEdges,
  setNodes,
  setEdges,
  syncDraftState,
  rehydrateHandlers,
  onAttachedEffect,
}: UseUndoHistoryOptions) {
  const undoStackRef = useRef<SerializedSnapshot[]>([]);
  const redoStackRef = useRef<SerializedSnapshot[]>([]);
  const isUndoRedoInProgressRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastUpdateSnapshotTimeRef = useRef(0);

  // Use state so UI can react to canUndo/canRedo
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const updateFlags = useCallback(() => {
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
  }, []);

  const debouncedSyncToBackend = useCallback(
    (snapshot: SerializedSnapshot) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        syncDraftState(toBackendPayload(snapshot)).catch((err) => {
          // The caller's `syncDraftState` (a React Query mutation) wires its
          // own onError → toast/Sentry, so we don't surface here. Still log
          // to the console so the trail is obvious when debugging locally.
          // eslint-disable-next-line no-console
          console.warn('[useUndoHistory] draft sync failed after undo/redo', err);
        });
      }, 500);
    },
    [syncDraftState]
  );

  /**
   * Capture the current canvas state before a mutation.
   * Call this as the **first line** of each mutation handler.
   */
  const captureSnapshot = useCallback(
    (
      description: string,
      options?: { debounceMs?: number; attachedEffects?: AttachedEffect[] },
    ) => {
      if (isUndoRedoInProgressRef.current) return;

      const nodes = getNodes();
      // Skip if no persisted nodes
      if (nodes.filter((n) => !isTempNodeId(n.id)).length === 0) return;

      // Debounce support for rapid updates (e.g. onUpdateNode)
      if (options?.debounceMs) {
        const now = Date.now();
        if (now - lastUpdateSnapshotTimeRef.current < options.debounceMs) return;
        lastUpdateSnapshotTimeRef.current = now;
      }

      const edges = getEdges();
      const snapshot: SerializedSnapshot = {
        ...serializeSnapshot(description, nodes, edges),
        attachedEffects: options?.attachedEffects,
      };

      undoStackRef.current = [...undoStackRef.current.slice(-(MAX_SNAPSHOTS - 1)), snapshot];
      // New action clears redo
      redoStackRef.current = [];
      updateFlags();
    },
    [getNodes, getEdges, updateFlags]
  );

  const undo = useCallback((): string | null => {
    const stack = undoStackRef.current;
    if (stack.length === 0) return null;

    const snapshot = stack[stack.length - 1]!;
    undoStackRef.current = stack.slice(0, -1);

    // Push current state onto redo
    const currentNodes = getNodes();
    const currentEdges = getEdges();
    const currentSnapshot = serializeSnapshot('(current)', currentNodes, currentEdges);
    redoStackRef.current = [...redoStackRef.current, currentSnapshot];

    // Restore
    isUndoRedoInProgressRef.current = true;
    try {
      setNodes(rehydrateNodes(snapshot, rehydrateHandlers));
      setEdges(rehydrateEdges(snapshot));
    } finally {
      isUndoRedoInProgressRef.current = false;
    }

    debouncedSyncToBackend(snapshot);
    if (snapshot.attachedEffects && onAttachedEffect) {
      for (const effect of snapshot.attachedEffects) onAttachedEffect(effect);
    }
    updateFlags();
    return snapshot.description;
  }, [
    getNodes,
    getEdges,
    setNodes,
    setEdges,
    rehydrateHandlers,
    debouncedSyncToBackend,
    onAttachedEffect,
    updateFlags,
  ]);

  const redo = useCallback((): string | null => {
    const stack = redoStackRef.current;
    if (stack.length === 0) return null;

    const snapshot = stack[stack.length - 1]!;
    redoStackRef.current = stack.slice(0, -1);

    // Push current state onto undo
    const currentNodes = getNodes();
    const currentEdges = getEdges();
    const currentSnapshot = serializeSnapshot('(current)', currentNodes, currentEdges);
    undoStackRef.current = [...undoStackRef.current, currentSnapshot];

    // Restore
    isUndoRedoInProgressRef.current = true;
    try {
      setNodes(rehydrateNodes(snapshot, rehydrateHandlers));
      setEdges(rehydrateEdges(snapshot));
    } finally {
      isUndoRedoInProgressRef.current = false;
    }

    debouncedSyncToBackend(snapshot);
    updateFlags();
    return snapshot.description;
  }, [
    getNodes,
    getEdges,
    setNodes,
    setEdges,
    rehydrateHandlers,
    debouncedSyncToBackend,
    updateFlags,
  ]);

  const clearHistory = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    updateFlags();
  }, [updateFlags]);

  return {
    captureSnapshot,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
    /** True while an undo/redo restore is in progress (use to guard snapshot capture) */
    isUndoRedoInProgress: isUndoRedoInProgressRef,
  };
}

export type UndoHistoryHandle = ReturnType<typeof useUndoHistory>;
